import {
    Divider,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    MenuList,
    MenuProps,
    Slider,
} from "@mui/material";

import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BrightnessAutoIcon from "@mui/icons-material/BrightnessAuto";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import DownloadingIcon from "@mui/icons-material/Downloading";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import TuneIcon from "@mui/icons-material/Tune";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import Logout from "@mui/icons-material/Logout";
import UpdateIcon from "@mui/icons-material/Update";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { userSelectors } from "./store/selectors";
import { userActions } from "./store/slice";

import { liveStreamingSelectors } from "features/liveStreaming/store/selectors";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { MsalInstanceService } from "features/auth/MsalInstanceService";
import { ColorScheme } from "./types/ColorScheme";
import { LiveStreamFeedType } from "features/liveStreaming/types/LiveStreamFeedType";
import { JobRunsDialog } from "features/jobs/JobRunsDialog";

export default function UserMenu(props: MenuProps) {
    const dispatch = useAppDispatch();

    // state
    const isSavingUserSettings = useAppSelector(
        userSelectors.selectIsSavingUserSettings,
    );

    // color scheme
    const userSettingsColorScheme = useAppSelector(
        userSelectors.selectColorScheme,
    );

    let colorSchemeIcon = <BrightnessAutoIcon fontSize="small" />;
    let colorSchemeText = "System theme";

    switch (userSettingsColorScheme) {
        case ColorScheme.Dark:
            colorSchemeIcon = <Brightness7Icon fontSize="small" />;
            colorSchemeText = "Dark theme";
            break;

        case ColorScheme.Light:
            colorSchemeIcon = <Brightness4Icon fontSize="small" />;
            colorSchemeText = "Light theme";
            break;
    }

    // audio enabled by default
    const audioEnabledByDefault = useAppSelector(
        userSelectors.selectAudioEnabledByDefault,
    );

    // grid sizing
    const gridViewEnableAutoSizing = useAppSelector(
        userSelectors.selectGridViewEnableAutoSizing,
    );
    const gridViewMaxNumberOfDevicesPerGridRow = useAppSelector(
        userSelectors.selectGridViewMaxNumberOfDevicesPerGridRow,
    );

    // livestream restrictions
    const showUnrestrictedFeedsByDefault = useAppSelector(
        userSelectors.selectShowUnrestrictedFeedsByDefault,
    );
    const allowedLiveStreamFeedTypes = useAppSelector(
        liveStreamingSelectors.selectAllowedLiveStreamFeedTypes,
    );

    // playback debug tools
    const debugToolsShowBufferingRecordingsByDefault = useAppSelector(
        userSelectors.selectDebugToolsShowBufferingRecordingsByDefault,
    );
    const debugToolsShowForwardAndReversedRecordingsByDefault = useAppSelector(
        userSelectors.selectDebugToolsShowForwardAndReversedRecordingsByDefault,
    );
    const debugToolsShowVideoDirectionInformationByDefault = useAppSelector(
        userSelectors.selectDebugToolsShowVideoDirectionInformationByDefault,
    );
    const debugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault =
        useAppSelector(
            userSelectors.selectDebugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault,
        );
    const debugToolsShowStatisticsByDefault = useAppSelector(
        userSelectors.selectDebugToolsShowStatisticsByDefault,
    );
    const debugToolsShowSystemControlsByDefault = useAppSelector(
        userSelectors.selectDebugToolsShowSystemControlsByDefault,
    );

    // status
    const showJobRunsDialog = useAppSelector(
        userSelectors.selectShowJobRunsDialog,
    );

    return (
        <>
            <Menu {...props}>
                <MenuList sx={{ minWidth: 250 }}>
                    <MenuItem
                        disabled={isSavingUserSettings}
                        onClick={() => {
                            dispatch(
                                userActions.selectNextAvailableColorScheme(),
                            );
                        }}
                    >
                        <ListItemIcon>{colorSchemeIcon}</ListItemIcon>
                        <ListItemText>{colorSchemeText}</ListItemText>
                    </MenuItem>

                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[
                            AppFeature.DeviceAudioSupport,
                        ]}
                    >
                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleAudioEnabledByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                {!audioEnabledByDefault ? (
                                    <VolumeOffIcon fontSize="small" />
                                ) : (
                                    <VolumeUpIcon fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText>
                                Sound{" "}
                                {audioEnabledByDefault ? "enabled" : "disabled"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>
                    </FeatureFlagGuard>

                    <MenuItem
                        disabled={isSavingUserSettings}
                        onClick={() => {
                            dispatch(
                                userActions.toggleGridViewEnableAutoSizing(),
                            );
                        }}
                    >
                        <ListItemIcon>
                            {!gridViewEnableAutoSizing ? (
                                <CheckBoxOutlineBlankIcon fontSize="small" />
                            ) : (
                                <CheckBoxIcon fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText>Grid view auto-sizing </ListItemText>
                    </MenuItem>
                    <MenuItem disabled={!gridViewEnableAutoSizing}>
                        <Slider
                            value={gridViewMaxNumberOfDevicesPerGridRow}
                            step={1}
                            min={1}
                            max={6}
                            marks
                            valueLabelDisplay={"auto"}
                            onChange={(event, value) => {
                                const newValue = Array.isArray(value)
                                    ? value[0]
                                    : value;
                                dispatch(
                                    userActions.toggleGridViewMaxNumberOfDevicesPerGridRow(
                                        newValue,
                                    ),
                                );
                            }}
                        />
                    </MenuItem>
                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[
                            AppFeature.LiveStreamingStreamAnonymization,
                        ]}
                    >
                        {allowedLiveStreamFeedTypes.length > 1 &&
                            allowedLiveStreamFeedTypes.some(
                                (x) => x === LiveStreamFeedType.Original,
                            ) && (
                                <MenuItem
                                    disabled={isSavingUserSettings}
                                    onClick={() => {
                                        dispatch(
                                            userActions.toggleShowUnrestrictedFeedsByDefault(),
                                        );
                                    }}
                                >
                                    <ListItemIcon>
                                        <TheaterComedyIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        Unrestricted feeds{" "}
                                        {showUnrestrictedFeedsByDefault
                                            ? "shown"
                                            : "hidden"}{" "}
                                        by default
                                    </ListItemText>
                                </MenuItem>
                            )}
                    </FeatureFlagGuard>
                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[
                            AppFeature.AdminDebugTools,
                        ]}
                    >
                        <Divider />

                        <ListSubheader>Playback debug tools</ListSubheader>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowStatisticsByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <QueryStatsIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Statistics{" "}
                                {debugToolsShowStatisticsByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowBufferingRecordingsByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <DownloadingIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Buffering recordings{" "}
                                {debugToolsShowBufferingRecordingsByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowForwardAndReversedRecordingsByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <SwapHorizIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Forward and reversed recordings{" "}
                                {debugToolsShowForwardAndReversedRecordingsByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowVideoDirectionInformationByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <SwapHorizontalCircleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Video direction information{" "}
                                {debugToolsShowVideoDirectionInformationByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowSystemControlsByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <TuneIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                System controls{" "}
                                {debugToolsShowSystemControlsByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>

                        <MenuItem
                            disabled={isSavingUserSettings}
                            onClick={() => {
                                dispatch(
                                    userActions.toggleDebugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault(),
                                );
                            }}
                        >
                            <ListItemIcon>
                                <FilterAltOffIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                All recordings{" "}
                                {debugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault
                                    ? "shown"
                                    : "hidden"}{" "}
                                by default
                            </ListItemText>
                        </MenuItem>
                    </FeatureFlagGuard>

                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[AppFeature.Jobs]}
                    >
                        <Divider />

                        <ListSubheader>Status</ListSubheader>

                        <MenuItem
                            onClick={() => {
                                dispatch(userActions.toggleJobRunsDialog());
                            }}
                        >
                            <ListItemIcon>
                                <UpdateIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Latest job runs</ListItemText>
                        </MenuItem>
                    </FeatureFlagGuard>

                    <Divider />

                    <MenuItem
                        onClick={() =>
                            MsalInstanceService.getInstance().logoutAsync()
                        }
                    >
                        <ListItemIcon>
                            <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Logout</ListItemText>
                    </MenuItem>
                </MenuList>
            </Menu>

            {showJobRunsDialog && (
                <JobRunsDialog
                    onClose={() => {
                        dispatch(userActions.toggleJobRunsDialog());
                    }}
                />
            )}
        </>
    );
}
